import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ChevronLeft, Search, Loader2, ShieldAlert, Ban, RotateCcw, UserX, Trash2,
  ShieldCheck, AlertTriangle, History,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import {
  fetchAdminUsuarios, suspenderUsuario, reactivarUsuario, anonimizarUsuario,
  eliminarUsuario, fetchAdminAuditoria,
} from "../lib/admin";
import { humanizeAxiosError } from "../lib/api";

const ROL_LABEL = { cliente: "Cliente", contratista: "Maestro-Contratista", ferreteria: "Ferretería", admin: "Administrador" };

const ACCIONES = {
  suspender: { titulo: "Suspender usuario", verbo: "Suspender", desc: "El usuario no podrá iniciar sesión. Sus datos se conservan y la acción es reversible.", tono: "amber", fn: suspenderUsuario },
  reactivar: { titulo: "Reactivar usuario", verbo: "Reactivar", desc: "El usuario volverá a tener acceso a la plataforma.", tono: "emerald", fn: reactivarUsuario },
  anonimizar: { titulo: "Anonimizar usuario", verbo: "Anonimizar", desc: "Se conservará su contenido pero se eliminará su identidad (nombre, correo, foto). No podrá iniciar sesión. Irreversible.", tono: "orange", fn: anonimizarUsuario },
  eliminar: { titulo: "Eliminar usuario", verbo: "Eliminar definitivamente", desc: "Se borrará el usuario y EN CASCADA todo lo que creó: publicaciones, chats, portafolio, tienda/productos, cotizaciones, favoritos, notificaciones, compras y archivos. Irreversible.", tono: "red", fn: eliminarUsuario },
};

function EstadoBadge({ estado }) {
  const map = {
    activo: "bg-emerald-50 text-emerald-600",
    suspendido: "bg-amber-50 text-amber-700",
    anonimizado: "bg-slate-200 text-slate-600",
  };
  return <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded ${map[estado] || map.activo}`} data-testid={`estado-badge-${estado}`}>{estado}</span>;
}

function ConfirmModal({ accionKey, usuario, onClose, onDone }) {
  const [motivo, setMotivo] = useState("");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const cfg = ACCIONES[accionKey];
  const isDanger = accionKey === "eliminar" || accionKey === "anonimizar";

  async function confirmar() {
    if (motivo.trim().length < 3) { setErr("El motivo es obligatorio (mínimo 3 caracteres)."); return; }
    setSaving(true); setErr("");
    try {
      await cfg.fn(usuario.id, motivo.trim());
      onDone(`${cfg.verbo} · ${usuario.email}`);
    } catch (e) {
      setErr(humanizeAxiosError(e));
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4" data-testid="confirm-modal">
      <div className="w-full max-w-md rounded-2xl bg-white shadow-xl p-6">
        <div className="flex items-center gap-3">
          <span className={`w-10 h-10 rounded-xl flex items-center justify-center ${isDanger ? "bg-red-50 text-brand-red" : "bg-amber-50 text-amber-600"}`}>
            {isDanger ? <AlertTriangle size={20} /> : <ShieldCheck size={20} />}
          </span>
          <div>
            <h3 className="font-bold text-slate-900">{cfg.titulo}</h3>
            <p className="text-xs text-slate-500">{usuario.name} · {usuario.email}</p>
          </div>
        </div>
        <p className="mt-4 text-sm text-slate-600">{cfg.desc}</p>
        <label className="mt-4 block text-xs font-bold uppercase tracking-wide text-slate-500">Motivo (obligatorio)</label>
        <textarea
          data-testid="motivo-input"
          value={motivo}
          onChange={(e) => setMotivo(e.target.value)}
          rows={3}
          placeholder="Ej. Reporte de mala conducta / spam / solicitud del usuario…"
          className="mt-1 w-full rounded-xl border border-slate-200 p-3 text-sm focus:border-brand-blue focus:outline-none"
        />
        {err && <p className="mt-2 text-sm text-brand-red" data-testid="modal-error">{err}</p>}
        <div className="mt-5 flex gap-3 justify-end">
          <button data-testid="modal-cancel" onClick={onClose} disabled={saving} className="px-4 h-10 rounded-xl border border-slate-200 text-sm font-semibold text-slate-600 hover:bg-slate-50">Cancelar</button>
          <button
            data-testid="modal-confirm"
            onClick={confirmar}
            disabled={saving}
            className={`px-4 h-10 rounded-xl text-sm font-semibold text-white inline-flex items-center gap-2 disabled:opacity-60 ${isDanger ? "bg-brand-red hover:bg-red-700" : "bg-brand-blue hover:bg-blue-700"}`}
          >
            {saving ? <Loader2 size={16} className="animate-spin" /> : null}
            {cfg.verbo}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AdminUsuariosPage() {
  const { user } = useAuth();
  const [q, setQ] = useState("");
  const [estado, setEstado] = useState("");
  const [rol, setRol] = useState("");
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // { accionKey, usuario }
  const [flash, setFlash] = useState("");
  const [auditoria, setAuditoria] = useState([]);
  const [showAudit, setShowAudit] = useState(false);
  const [auditLoading, setAuditLoading] = useState(false);

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchAdminUsuarios({ q, estado, rol, page: 1, page_size: 50 });
      setData(res);
    } catch { /* noop */ } finally { setLoading(false); }
  }, [q, estado, rol]);

  useEffect(() => { const t = setTimeout(cargar, 300); return () => clearTimeout(t); }, [cargar]);

  async function cargarAuditoria() {
    const next = !showAudit;
    setShowAudit(next);
    if (next) {
      setAuditLoading(true);
      try { setAuditoria(await fetchAdminAuditoria(50)); } catch { /* noop */ } finally { setAuditLoading(false); }
    }
  }

  if (user && user.role !== "admin") {
    return (
      <div className="max-w-lg mx-auto text-center py-20" data-testid="admin-denegado">
        <ShieldAlert size={40} className="mx-auto text-brand-red" />
        <h2 className="mt-4 text-xl font-bold text-slate-900">Acceso restringido</h2>
        <p className="mt-2 text-slate-500">Esta sección es exclusiva para administradores.</p>
        <Link to="/" className="mt-4 inline-block text-brand-blue font-semibold hover:underline">Volver al inicio</Link>
      </div>
    );
  }

  return (
    <div className="px-5 md:px-8 py-8" data-testid="admin-usuarios-page">
      <Link to="/admin" className="inline-flex items-center gap-1 text-sm font-semibold text-slate-500 hover:text-slate-800">
        <ChevronLeft size={16} /> Volver al Panel
      </Link>
      <header className="mt-4 mb-6">
        <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-brand-red">Administración</p>
        <h1 className="mt-1 text-3xl font-extrabold text-slate-900 tracking-tight">Gestión de Usuarios</h1>
        <p className="mt-1 text-slate-500 text-sm">Suspende, anonimiza o elimina cuentas por reportes de mala conducta. Toda acción queda auditada.</p>
      </header>

      {flash && (
        <div className="mb-4 rounded-xl bg-emerald-50 border border-emerald-200 px-4 py-2.5 text-sm text-emerald-700 font-medium" data-testid="flash-msg">{flash}</div>
      )}

      {/* Filtros */}
      <div className="mb-4 flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            data-testid="search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar por nombre o correo…"
            className="w-full h-11 pl-9 pr-3 rounded-xl border border-slate-200 text-sm focus:border-brand-blue focus:outline-none"
          />
        </div>
        <select data-testid="filter-estado" value={estado} onChange={(e) => setEstado(e.target.value)} className="h-11 px-3 rounded-xl border border-slate-200 text-sm text-slate-600">
          <option value="">Todos los estados</option>
          <option value="activo">Activos</option>
          <option value="suspendido">Suspendidos</option>
          <option value="anonimizado">Anonimizados</option>
        </select>
        <select data-testid="filter-rol" value={rol} onChange={(e) => setRol(e.target.value)} className="h-11 px-3 rounded-xl border border-slate-200 text-sm text-slate-600">
          <option value="">Todos los roles</option>
          <option value="cliente">Cliente</option>
          <option value="contratista">Maestro-Contratista</option>
          <option value="ferreteria">Ferretería</option>
          <option value="admin">Administrador</option>
        </select>
      </div>

      {/* Tabla */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-soft overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16 text-slate-500"><Loader2 className="animate-spin mr-2" /> Cargando usuarios…</div>
        ) : data.items.length === 0 ? (
          <div className="py-16 text-center text-slate-400 text-sm" data-testid="empty-users">No se encontraron usuarios con esos filtros.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="users-table">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wide text-slate-400 border-b border-slate-100">
                  <th className="py-3 px-4">Usuario</th>
                  <th className="py-3 px-2">Rol</th>
                  <th className="py-3 px-2">Estado</th>
                  <th className="py-3 px-4 text-right">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((u) => (
                  <tr key={u.id} className="border-b border-slate-50 hover:bg-slate-50/50" data-testid={`user-row-${u.id}`}>
                    <td className="py-3 px-4">
                      <p className="font-semibold text-slate-800">{u.name || "—"}</p>
                      <p className="text-xs text-slate-400">{u.email}</p>
                    </td>
                    <td className="py-3 px-2">
                      <span className="text-xs text-slate-600">{ROL_LABEL[u.role] || u.role}</span>
                      {u.es_admin_principal && <span className="ml-1 text-[10px] font-bold text-brand-blue">★ principal</span>}
                    </td>
                    <td className="py-3 px-2"><EstadoBadge estado={u.estado} /></td>
                    <td className="py-3 px-4">
                      <div className="flex items-center justify-end gap-1.5">
                        {u.es_admin_principal ? (
                          <span className="text-[11px] text-slate-400 italic">Protegido</span>
                        ) : u.estado === "anonimizado" ? (
                          <button data-testid={`btn-eliminar-${u.id}`} onClick={() => setModal({ accionKey: "eliminar", usuario: u })} className="p-2 rounded-lg text-brand-red hover:bg-red-50" title="Eliminar"><Trash2 size={16} /></button>
                        ) : (
                          <>
                            {u.estado === "suspendido" ? (
                              <button data-testid={`btn-reactivar-${u.id}`} onClick={() => setModal({ accionKey: "reactivar", usuario: u })} className="p-2 rounded-lg text-emerald-600 hover:bg-emerald-50" title="Reactivar"><RotateCcw size={16} /></button>
                            ) : (
                              <button data-testid={`btn-suspender-${u.id}`} onClick={() => setModal({ accionKey: "suspender", usuario: u })} className="p-2 rounded-lg text-amber-600 hover:bg-amber-50" title="Suspender"><Ban size={16} /></button>
                            )}
                            <button data-testid={`btn-anonimizar-${u.id}`} onClick={() => setModal({ accionKey: "anonimizar", usuario: u })} className="p-2 rounded-lg text-orange-600 hover:bg-orange-50" title="Anonimizar"><UserX size={16} /></button>
                            <button data-testid={`btn-eliminar-${u.id}`} onClick={() => setModal({ accionKey: "eliminar", usuario: u })} className="p-2 rounded-lg text-brand-red hover:bg-red-50" title="Eliminar"><Trash2 size={16} /></button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <p className="mt-2 text-xs text-slate-400">{data.total} usuario(s) en total.</p>

      {/* Auditoría */}
      <div className="mt-8">
        <button data-testid="toggle-auditoria" onClick={cargarAuditoria} className="inline-flex items-center gap-2 text-sm font-semibold text-slate-600 hover:text-slate-900">
          <History size={16} /> {showAudit ? "Ocultar" : "Ver"} registro de auditoría
        </button>
        {showAudit && (
          <div className="mt-3 bg-white rounded-2xl border border-slate-100 shadow-soft p-5" data-testid="auditoria-panel">
            {auditLoading ? (
              <p className="text-sm text-slate-500 inline-flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Cargando registro…</p>
            ) : auditoria.length === 0 ? (
              <p className="text-sm text-slate-400">Sin acciones registradas todavía.</p>
            ) : (
              <ul className="space-y-2.5">
                {auditoria.map((a) => (
                  <li key={a.id} className="text-sm border-b border-slate-50 pb-2.5">
                    <span className="font-semibold text-slate-800 capitalize">{a.accion}</span>
                    <span className="text-slate-500"> · {a.target_email || a.target_nombre}</span>
                    <span className="text-slate-400 text-xs"> — por {a.actor_email} · {a.created_at ? new Date(a.created_at).toLocaleString("es-PE") : ""}</span>
                    <p className="text-xs text-slate-500 mt-0.5">Motivo: {a.motivo}</p>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>

      {modal && (
        <ConfirmModal
          accionKey={modal.accionKey}
          usuario={modal.usuario}
          onClose={() => setModal(null)}
          onDone={(msg) => {
            setModal(null);
            setFlash(msg);
            setAuditoria([]);
            if (showAudit) fetchAdminAuditoria(50).then(setAuditoria).catch(() => {});
            cargar();
            setTimeout(() => setFlash(""), 5000);
          }}
        />
      )}
    </div>
  );
}
